import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { PrescriptionDetailsModal } from '../components/prescription/PrescriptionDetailsModal';
import { PrescriptionSuccessModal } from '../components/prescription/PrescriptionSuccessModal';
import { PrescriptionCancelBottomSheet } from '../components/prescription/PrescriptionCancelBottomSheet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trackPrescriptionCashOrder, trackPrescriptionInsuranceOrder, trackPrescriptionWasfatyOrder, trackPrescriptionPageView } from '../lib/analytics/events';
import { MapPinIcon, EditIcon, FileTextIcon, ImageIcon, CreditCardIcon, WalletIcon, BanknoteIcon, CheckCircleIcon, ClockIcon, TruckIcon, XCircleIcon, PackageIcon, ChevronRightIcon, InfoIcon } from 'lucide-react';
import { prescriptionRecords } from '../data/prescriptionRecords';
type Beneficiary = {
  id: 'self' | 'sara';
  name: string;
  detail: string;
};
const beneficiaries: Beneficiary[] = [{
  id: 'self',
  name: 'Ahmed Mohammed',
  detail: 'Me · Bupa Arabia'
}, {
  id: 'sara',
  name: 'Sara Mohammed',
  detail: 'Daughter · Bupa Arabia'
}];
export function PrescriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPrescriptionType = searchParams.get('type');
  const [selectedPrescriptionType, setSelectedPrescriptionType] = useState(initialPrescriptionType === 'insurance' || initialPrescriptionType === 'wasfaty' || initialPrescriptionType === 'cash' ? initialPrescriptionType : '');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedOutOfStock, setSelectedOutOfStock] = useState('');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary['id'] | ''>('');
  const [prescriptionDetails, setPrescriptionDetails] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPrescription, setSelectedPrescription] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [wasfatyCustomerName, setWasfatyCustomerName] = useState('');
  const [wasfatyNationalId, setWasfatyNationalId] = useState('');
  const [wasfatyNumber, setWasfatyNumber] = useState('');
  const [insuranceCustomerName, setInsuranceCustomerName] = useState('');
  const [insuranceNationalId, setInsuranceNationalId] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const fulfillmentMethod = 'delivery' as const;
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const recentPrescriptions = prescriptionRecords;
  const isFormValid = () => {
    const baseValid = selectedBeneficiary && selectedPrescriptionType && (prescriptionDetails || selectedFile) && selectedPaymentMethod && selectedOutOfStock;
    if (!baseValid) return false;
    if (selectedPrescriptionType === 'wasfaty') {
      return wasfatyCustomerName && wasfatyNationalId && wasfatyNumber;
    }
    if (selectedPrescriptionType === 'insurance') {
      return insuranceCustomerName && insuranceNationalId && insuranceProvider;
    }
    return true;
  };
  // Determine next required step (label + section id to scroll to)
  const getNextRequiredStep = (): {
    label: string;
    sectionId: string;
  } | null => {
    if (!selectedBeneficiary) {
      return {
        label: 'Choose who this prescription is for',
        sectionId: 'section-beneficiary'
      };
    }
    if (!selectedPrescriptionType) {
      return {
        label: 'Select Prescription Type',
        sectionId: 'section-prescription-type'
      };
    }
    if (selectedPrescriptionType === 'wasfaty') {
      if (!wasfatyCustomerName || !wasfatyNationalId || !wasfatyNumber) {
        return {
          label: 'Complete Wasfaty Information',
          sectionId: 'section-prescription-type'
        };
      }
    }
    if (selectedPrescriptionType === 'insurance') {
      if (!insuranceCustomerName || !insuranceNationalId || !insuranceProvider) return {
        label: 'Complete Insurance Information',
        sectionId: 'section-prescription-type'
      };
    }
    if (!prescriptionDetails && !selectedFile) {
      return {
        label: 'Upload Prescription',
        sectionId: 'section-upload'
      };
    }
    if (!selectedPaymentMethod) {
      return {
        label: 'Select Payment Method',
        sectionId: 'section-payment'
      };
    }
    if (!selectedOutOfStock) {
      return {
        label: 'Choose Out-of-Stock Option',
        sectionId: 'section-out-of-stock'
      };
    }
    return null;
  };
  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const headerOffset = window.innerWidth >= 768 ? 220 : 120;
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
    // Add a brief highlight pulse
    el.classList.add('ring-2', 'ring-[#1E2B55]', 'ring-offset-2');
    setTimeout(() => {
      el.classList.remove('ring-2', 'ring-[#1E2B55]', 'ring-offset-2');
    }, 1500);
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-7603" data-mp="2u030hnor4075" />;
      case 'ready':
        return <PackageIcon className="w-4 h-4 text-blue-500" data-id="element-7604" data-mp="3hpxc61xc3ipt" />;
      case 'in-progress':
      case 'expired':
        return <ClockIcon className="w-4 h-4 text-yellow-500" data-id="element-7605" data-mp="3mps2o4d766ac" />;
      case 'insurance-rejected':
      default:
        return <XCircleIcon className="w-4 h-4 text-red-500" data-id="element-7606" data-mp="38opglrm7cvzf" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready';
      case 'in-progress':
        return 'In Progress';
      case 'expired':
        return 'Prescription expired';
      case 'insurance-rejected':
        return 'Insurance claim rejected';
      default:
        return 'Rejected';
    }
  };
  const handlePrescriptionClick = (prescription: any) => {
    setSelectedPrescription(prescription);
    setIsModalOpen(true);
  };
  const handleViewAllClick = () => {
    navigate('/prescription/history');
  };
  const handleViewHistoryClick = () => {
    // Track analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      ;
      (window as any).gtag('event', 'prescription_history_click', {
        page: 'prescription',
        device: 'mobile'
      });
    }
    navigate('/prescription/history');
  };
  // Track prescription page view on mount
  useEffect(() => {
    if (selectedPrescriptionType) {
      trackPrescriptionPageView(selectedPrescriptionType as 'cash' | 'insurance' | 'wasfaty', 'start');
    }
  }, [selectedPrescriptionType]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast.error('Please complete all required fields', {
        duration: 5000
      });
      return;
    }
    if (!selectedFile && !prescriptionDetails) {
      toast.error('Please upload prescription file or enter details', {
        duration: 5000
      });
      return;
    }
    setIsSubmitting(true);
    // Track analytics based on prescription type
    // Generate a reference number and record the selected prescription flow.
    const generatedReference = `#RX-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 89999)}`;
    if (selectedPrescriptionType === 'cash') {
      trackPrescriptionCashOrder(generatedReference, 0, fulfillmentMethod);
    } else if (selectedPrescriptionType === 'insurance') {
      trackPrescriptionInsuranceOrder(generatedReference, 0, insuranceProvider, fulfillmentMethod);
    } else if (selectedPrescriptionType === 'wasfaty') {
      trackPrescriptionWasfatyOrder(generatedReference, 0, fulfillmentMethod);
    }
    // Generate a reference number and show the detailed success state
    setReferenceNumber(generatedReference);
    setShowSuccessModal(true);
    setIsSubmitting(false);
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-7607" data-mp="2nz402isdhbv4">
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7608" data-mp="2jz25suoyvkpv">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7609" data-mp="2ezz3hi0dfsll">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-7610" data-mp="2f10q8jqr23j5" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7611" data-mp="32x4qixahevhv">
          <h1 className="sr-only" data-id="element-7612" data-mp="2xxs7knmqe8kk">
            Upload Your Prescription - Fast & Secure Medicine Delivery
          </h1>
          {/* Mobile Header Overlay */}
          <MobileInternalHeader title="Prescription Services" data-id="element-7613" data-mp="3qnin9140hiis" />
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Prescription Services'
        }]} title="Prescription Services" description="Upload your prescription and get your medications delivered quickly and securely." data-id="element-7614" data-mp="u4tx9acpgfb6" />
          <div className="container mx-auto px-4 md:px-8 lg:px-12 py-8" data-id="element-7615" data-mp="1d3odyovqbptz">
            {/* View Prescription History Button - Mobile Only */}
            <div className="md:hidden mb-6" data-id="element-7616" data-mp="1841w224yhleu">
              <button onClick={handleViewHistoryClick} className="w-full bg-white border-2 border-[#1E2B55] text-[#1E2B55] font-bold py-3 px-4 rounded-lg hover:bg-[#1E2B55] hover:text-white transition-all flex items-center justify-center gap-2" aria-label="Open prescription history" data-id="element-7617" data-mp="1mtrd76tg200r">
                <FileTextIcon className="w-5 h-5" data-id="element-7618" data-mp="1qwnosdkkgvv7" />
                View Prescription History
              </button>
            </div>
            <div className="flex flex-col lg:flex-row gap-6" data-id="element-7619" data-mp="2afbjalunjpaz">
              {/* Sidebar */}
              <div className="lg:w-80 flex-shrink-0 space-y-6" data-id="element-7620" data-mp="2fe4wse9xvgb5">
                {/* How It Works */}
                <div className="bg-white rounded-xl shadow-md p-6 sticky top-24 hidden md:block" data-id="element-7621" data-mp="21ij0dm1dlu8p">
                  <h3 className="text-xl font-bold mb-6 text-center" data-id="element-7622" data-mp="25k4k9rcbv8se">
                    How It Works
                  </h3>
                  <div className="space-y-6" data-id="element-7623" data-mp="1wh3ghxaxptrw">
                    <div className="flex items-start" data-id="element-7624" data-mp="1wiy27du15px1">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-7625" data-mp="1hsqp7dmskz3q">
                        1
                      </div>
                      <div className="ml-4" data-id="element-7626" data-mp="18s0wwjwqpqur">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-7627" data-mp="18saqehgzug57">
                          Upload Prescription
                        </h4>
                        <p className="text-xs text-gray-600" data-id="element-7628" data-mp="3fg1d8j0lwtia">
                          Take a clear photo or scan your prescription
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-7629" data-mp="35pyj2fhr19id">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-7630" data-mp="35qhrn5lieljq">
                        2
                      </div>
                      <div className="ml-4" data-id="element-7631" data-mp="28q546q56tsx">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-7632" data-mp="3tfat1hkwhllq">Verification</h4>
                        <p className="text-xs text-gray-600" data-id="element-7633" data-mp="3pe7d6v2urpki">
                          Our pharmacist reviews and verifies your prescription
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-7634" data-mp="2dzlhicqu2v05">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-7635" data-mp="2dwz785ck61e8">
                        3
                      </div>
                      <div className="ml-4" data-id="element-7636" data-mp="oftxupqrnkie">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-7637" data-mp="tghv3t5l8xl2">
                          Order Confirmation
                        </h4>
                        <p className="text-xs text-gray-600" data-id="element-7638" data-mp="xh1ruvm91z6o">
                          Receive confirmation and proceed to checkout
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-7639" data-mp="17aipkapibyfm">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-7640" data-mp="1c6puc06633vz">
                        4
                      </div>
                      <div className="ml-4" data-id="element-7641" data-mp="1l8gyh0l272nq">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-7642" data-mp="1l636j0loe6ke">Delivery</h4>
                        <p className="text-xs text-gray-600" data-id="element-7643" data-mp="3izjmvj1lz1qo">
                          Get your medicines delivered to your doorstep
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Recent Prescriptions */}
                <div className="bg-white rounded-xl shadow-md p-6 hidden md:block" data-id="element-7644" data-mp="2nn8ywqifjwlc">
                  <div className="flex items-center justify-between mb-4" data-id="element-7645" data-mp="31m8qo718orlq">
                    <h3 className="text-lg font-bold" data-id="element-7646" data-mp="2wnyr7k8hf4ub">Recent Prescriptions</h3>
                    <button onClick={handleViewAllClick} className="text-xs font-medium text-[#1E2B55] hover:underline" data-id="element-7647" data-mp="28yw5a06tepu0">
                      View All
                    </button>
                  </div>
                  <div className="space-y-3" data-id="element-7648" data-mp="2iq0kn10ekcpd">
                    {recentPrescriptions.slice(0, 3).map(prescription => <button key={prescription.id} onClick={() => handlePrescriptionClick(prescription)} className="w-full border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-[#1E2B55] transition-all text-left" data-id="element-7649" data-mp="1q8eo8svchioh">
                        <div className="flex items-center justify-between mb-2" data-id="element-7650" data-mp="5i9a8e30xbt1">
                          <span className="text-xs font-bold text-gray-900" data-id="element-7651" data-mp="3nw8t1bn2pvfh">
                            {prescription.id}
                          </span>
                          <div className="flex items-center" data-id="element-7652" data-mp="3rxu02gcxffhr">
                            {getStatusIcon(prescription.status)}
                            <span className="text-xs ml-1" data-id="element-7653" data-mp="ogvbpu2gd4xh">
                              {getStatusText(prescription.status)}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-2" data-id="element-7654" data-mp="ffnp499atsj8">
                          {new Date(prescription.date).toLocaleDateString()}
                        </p>
                        <div className="text-xs text-gray-500" data-id="element-7655" data-mp="2v9o046qy9b9t">
                          {prescription.items.length} item(s)
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>
              {/* Main Form */}
              <div className="flex-1 space-y-6" data-id="element-7656" data-mp="w0cz5iitiezb">
                <section id="section-fulfillment" className="scroll-mt-32 rounded-xl border border-[#b8e2df] bg-[#f2fbfa] p-4 md:p-6" aria-labelledby="fulfillment-title" data-id="element-7657" data-mp="vz1w5kx75b99">
                  <div className="flex items-start gap-3" data-id="element-7658" data-mp="n07sjalg7km7">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand text-white" data-id="element-7659" data-mp="mxlldc2gz74i"><TruckIcon className="h-5 w-5" aria-hidden="true" data-id="element-7660" data-mp="i2oeobsnx9us" /></span>
                    <div className="min-w-0 flex-1" data-id="element-7661" data-mp="d20mhmsp6myz"><h2 id="fulfillment-title" className="text-xl font-bold text-gray-900" data-id="element-7662" data-mp="d3bp3jaepooi">Home delivery</h2><p className="mt-1 text-sm text-gray-600" data-id="element-7663" data-mp="9201tkf09fjh">Your medicines will be delivered to your selected address.</p></div>
                  </div>
                  <div className="mt-4 flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4" data-id="element-7664" data-mp="23ezlbomiatw1">
                    <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-7665" data-mp="3larwknx225v1" />
                    <div className="min-w-0 flex-1" data-id="element-7666" data-mp="3q9cv8iwnshhd"><span className="text-xs font-bold text-brand" data-id="element-7667" data-mp="3q8jkl6ukx7ty">DELIVERY ADDRESS</span><p className="mt-1 font-medium text-gray-900" data-id="element-7668" data-mp="3v2nsptfkzlwm">Riyadh, Al Malqa District, King Fahd Road</p><p className="mt-1 text-sm text-gray-600" data-id="element-7669" data-mp="2wioy63p5l4h">Ahmed Mohammed · +966 50 123 4567</p></div>
                    <button type="button" onClick={() => setLocationModalOpen(true)} className="shrink-0 text-sm font-bold text-brand transition-colors duration-150 hover:text-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-7670" data-mp="32e0e2gpc7us1">Change</button>
                  </div>
                </section>
                <section id="section-beneficiary" className="scroll-mt-32 rounded-xl border border-[#d9e5f2] bg-white p-5 shadow-sm" aria-labelledby="beneficiary-title" data-id="element-7671" data-mp="3gdrltzp9gexl">
                  <div data-id="element-7672" data-mp="vkxzafbvvf9h"><p className="text-xs font-bold uppercase tracking-[0.12em] text-[#256a88]" data-id="element-7673" data-mp="vkxwk7ki115s">Before you upload</p><h2 id="beneficiary-title" className="mt-1 text-xl font-bold text-gray-900" data-id="element-7674" data-mp="7aguctaqlvjf">Who is this prescription for?</h2><p className="mt-1 text-sm text-gray-600" data-id="element-7675" data-mp="2c5kiikfzl17">Select the beneficiary so we can use the right prescription, insurance and allergy profile.</p></div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-7676" data-mp="baqye0eyybvn">{beneficiaries.map(beneficiary => <button key={beneficiary.id} type="button" onClick={() => setSelectedBeneficiary(beneficiary.id)} className={`rounded-xl border-2 p-4 text-left transition-[border-color,background-color] duration-150 ${selectedBeneficiary === beneficiary.id ? 'border-[#16726c] bg-[#f2fbfa]' : 'border-gray-200 bg-white hover:border-[#b8e2df]'}`} data-id="element-7677" data-mp="q06qpku5epdg"><span className="block text-sm font-bold text-gray-900" data-id="element-7678" data-mp="uyzzizyhps7a">{beneficiary.name}</span><span className="mt-1 block text-xs text-gray-600" data-id="element-7679" data-mp="30ron1akwzl21">{beneficiary.detail}</span>{selectedBeneficiary === beneficiary.id && <span className="mt-3 inline-flex rounded-full bg-[#16726c] px-2 py-1 text-[10px] font-bold text-white" data-id="element-7680" data-mp="2wroa5udgrhx3">Selected</span>}</button>)}</div>
                  <button type="button" onClick={() => alert('Add family members from RX Health, then they will appear here.')} className="mt-4 text-sm font-bold text-[#16726c]" data-id="element-7681" data-mp="2mp271s13qmma">+ Add a family member</button>
                </section>
                {/* Prescription Type */}
                <div id="section-prescription-type" className="bg-white rounded-xl shadow-md p-6 transition-all scroll-mt-32" data-id="element-7682" data-mp="2hu4zyqndunc1">
                  <div className="flex items-center justify-between gap-3" data-id="element-7683" data-mp="27yl3o34wpmmr"><h2 className="text-xl font-bold" data-id="element-7684" data-mp="1tyac0xvrw40j">Prescription Type</h2>{selectedBeneficiary && <span className="rounded-full bg-[#f2fbfa] px-3 py-1 text-xs font-bold text-[#16726c]" data-id="element-7685" data-mp="1u0dc6aqn3diz">For {beneficiaries.find(item => item.id === selectedBeneficiary)?.name}</span>}</div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-7686" data-mp="3o9yu38xawmp5">
                    {[{
                    id: 'cash',
                    label: 'Cash Prescription',
                    icon: BanknoteIcon
                  }, {
                    id: 'wasfaty',
                    label: 'Wasfaty',
                    icon: FileTextIcon
                  }, {
                    id: 'insurance',
                    label: 'Insurance',
                    icon: WalletIcon
                  }].map(type => <button key={type.id} onClick={() => setSelectedPrescriptionType(type.id)} className={`border-2 rounded-lg p-4 text-center transition-[border-color,background-color] duration-150 ${selectedPrescriptionType === type.id ? 'border-brand bg-brand-light' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-7687" data-mp="1oezbvvt6p043">
                        <type.icon className={`w-8 h-8 mx-auto mb-2 ${selectedPrescriptionType === type.id ? 'text-brand' : 'text-gray-400'}`} data-id="element-7688" data-mp="33hhfzh2ixsd" />
                        <span className="text-sm font-medium" data-id="element-7689" data-mp="3moopqshy8fso">
                          {type.label}
                        </span>
                      </button>)}
                  </div>
                  {/* Wasfaty Additional Fields */}
                  {selectedPrescriptionType === 'wasfaty' && <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-7690" data-mp="38v74n39m6v0j">
                      <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-7691" data-mp="33wm4t4y9ydcm">
                        Wasfaty Information
                      </h3>
                      <div className="space-y-4" data-id="element-7692" data-mp="3dwd3litvbeii">
                        <div data-id="element-7693" data-mp="3dus5jj8chdfw">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7694" data-mp="2q6io0ti5zbuk">
                            Customer Name{' '}
                            <span className="text-red-500" data-id="element-7695" data-mp="2l7fuo6bjqu2f">*</span>
                          </label>
                          <input type="text" value={wasfatyCustomerName} onChange={e => setWasfatyCustomerName(e.target.value)} placeholder="Enter customer name" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7696" data-mp="3avl910hy84em" />
                        </div>
                        <div data-id="element-7697" data-mp="30xdvysqs66hw">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7698" data-mp="30x5j30unr16p">
                            National ID <span className="text-red-500" data-id="element-7699" data-mp="35xt6zdnlb1wp">*</span>
                          </label>
                          <input type="text" value={wasfatyNationalId} onChange={e => setWasfatyNationalId(e.target.value)} placeholder="Enter national ID" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7700" data-mp="g6wondrs1335" />
                        </div>
                        <div data-id="element-7701" data-mp="1myc0hyif8k3d">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7702" data-mp="1mzdfge2k5qqd">
                            Wasfaty Number{' '}
                            <span className="text-red-500" data-id="element-7703" data-mp="1i1bxjwgvbxgh">*</span>
                          </label>
                          <input type="text" value={wasfatyNumber} onChange={e => setWasfatyNumber(e.target.value)} placeholder="Enter Wasfaty number" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7704" data-mp="1d28rc8p5jl6x" />
                        </div>
                      </div>
                    </div>}
                  {/* Insurance Additional Fields */}
                  {selectedPrescriptionType === 'insurance' && <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-7705" data-mp="35f49mcv399zd">
                      <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-7706" data-mp="2roiqqo5mx01h">
                        Insurance Information
                      </h3>
                      <div className="space-y-4" data-id="element-7707" data-mp="2hr4mro0we3q2">
                        <div data-id="element-7708" data-mp="2hsfqxoy99dhp">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7709" data-mp="2hp9xxymi9rgp">
                            Customer Name{' '}
                            <span className="text-red-500" data-id="element-7710" data-mp="28q6e2sfil815">*</span>
                          </label>
                          <input type="text" value={insuranceCustomerName} onChange={e => setInsuranceCustomerName(e.target.value)} placeholder="Enter customer name" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7711" data-mp="2cs9rvnpnoopl" />
                        </div>
                        <div data-id="element-7712" data-mp="2d08wavl5p1wp">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7713" data-mp="2840ltvhwlwvv">
                            National ID <span className="text-red-500" data-id="element-7714" data-mp="2lvf8qdexmi87">*</span>
                          </label>
                          <input type="text" value={insuranceNationalId} onChange={e => setInsuranceNationalId(e.target.value)} placeholder="Enter national ID" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7715" data-mp="2hsscm0b8ab57" />
                        </div>
                        <div data-id="element-7716" data-mp="1xf5ur54d88gz">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7717" data-mp="227paa7mm5dof">
                            Insurance Provider{' '}
                            <span className="text-red-500" data-id="element-7718" data-mp="276k19wuqi8jm">*</span>
                          </label>
                          <select value={insuranceProvider} onChange={e => setInsuranceProvider(e.target.value)} className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7719" data-mp="2c4lj6egfc1ti">
                            <option value="" data-id="element-7720" data-mp="quwkv3hap1bj">Select insurance provider</option>
                            <option value="bupa" data-id="element-7721" data-mp="javnollfo96r">Bupa Arabia</option>
                            <option value="tawuniya" data-id="element-7722" data-mp="eiawlrtwwa6x">Tawuniya</option>
                            <option value="medgulf" data-id="element-7723" data-mp="9jhka2qd2p8c">MedGulf</option>
                            <option value="alrajhi" data-id="element-7724" data-mp="9jr93mzo6qcg">Al Rajhi Takaful</option>
                            <option value="saico" data-id="element-7725" data-mp="4lprl6hw6z33">SAICO</option>
                            <option value="other" data-id="element-7726" data-mp="127wvsfaaapex">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>}
                </div>
                {/* Upload Prescription */}
                <div id="section-upload" className="bg-white rounded-xl shadow-md p-4 md:p-6 transition-all scroll-mt-32" data-id="element-7727" data-mp="sc28v7z1286c">
                  <h2 className="text-lg md:text-xl font-bold mb-4" data-id="element-7728" data-mp="3d0ztoels84av">
                    Upload Prescription
                  </h2>
                  <aside className="mb-5 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" aria-label="Prescription upload requirements" data-id="element-7729" data-mp="390zdopj1bl1p">
                    <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" data-id="element-7730" data-mp="1gy4piesc1h2u" />
                    <div data-id="element-7731" data-mp="12yc1ir86xeyk">
                      <h3 className="text-sm font-bold text-amber-950" data-id="element-7732" data-mp="12z521agk3889">Before you upload</h3>
                      <ul className="mt-1 space-y-1 text-xs leading-5 text-amber-900" data-id="element-7733" data-mp="17wnehats85l0">
                        <li data-id="element-7734" data-mp="1vn9d6bhwcdic">Make sure the patient name, doctor’s stamp or signature, and issue date are clearly visible.</li>
                        <li data-id="element-7735" data-mp="1zpnrciamep3a">Prescriptions are valid for up to 30 days from the issue date.</li>
                        <li data-id="element-7736" data-mp="1lu28ksuzd1jb">Controlled medicines cannot be processed through this online service.</li>
                      </ul>
                    </div>
                  </aside>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-7737" data-mp="2ej95jnesdj2g">
                    {/* Prescription Image - Mobile-first */}
                    <div className="order-1" data-id="element-7738" data-mp="2sqcfkocesx6m">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7739" data-mp="2sp1c6pmvlrg1">
                        Prescription Image
                      </label>
                      {selectedFile ?
                    // Inline preview
                    <div className="relative border-2 border-[#1E2B55] rounded-lg overflow-hidden bg-gray-50" data-id="element-7740" data-mp="2xh2lc2oqz21u">
                          {selectedFile.type.startsWith('image/') ? <img src={URL.createObjectURL(selectedFile)} alt="Prescription preview" className="w-full h-48 md:h-56 object-contain bg-white" data-id="element-7741" data-mp="29tkrm1lx9tw8" /> : <div className="w-full h-48 md:h-56 flex flex-col items-center justify-center bg-white" data-id="element-7742" data-mp="2esxjx1vkbt67">
                              <FileTextIcon className="w-12 h-12 text-[#1E2B55] mb-2" data-id="element-7743" data-mp="3q600vy1kqkqw" />
                              <p className="text-sm font-medium text-gray-900 px-4 text-center break-all" data-id="element-7744" data-mp="1j10yc4sf0nbr">
                                {selectedFile.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-7745" data-mp="1a0cir5ndiewr">
                                {(selectedFile.size / 1024).toFixed(0)} KB
                              </p>
                            </div>}
                          <button onClick={() => setSelectedFile(null)} className="absolute top-2 right-2 bg-white/95 hover:bg-white text-red-600 rounded-full p-1.5 shadow-md transition-all" aria-label="Remove file" data-id="element-7746" data-mp="21qgy2pwa0br4">
                            <XCircleIcon className="w-5 h-5" data-id="element-7747" data-mp="1nzveeyyzzxs6" />
                          </button>
                          <div className="absolute bottom-2 left-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1" data-id="element-7748" data-mp="2lgu4obtcgz55">
                            <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-7749" data-mp="2o0qrzmiszuso" />
                            Uploaded
                          </div>
                        </div> : <>
                          {/* Mobile: Two big action buttons */}
                          <div className="md:hidden grid grid-cols-2 gap-3" data-id="element-7750" data-mp="3208ajk5iabtl">
                            <input type="file" accept="image/*" capture="environment" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="hidden" id="prescription-camera" data-id="element-7751" data-mp="36ytbjlsl0zj3" />
                            <label htmlFor="prescription-camera" className="flex flex-col items-center justify-center gap-2 bg-[#1E2B55] active:bg-[#162041] text-white font-semibold py-5 rounded-lg cursor-pointer transition-colors" data-id="element-7752" data-mp="3pqvsyzd1sjue">
                              <ImageIcon className="w-7 h-7" data-id="element-7753" data-mp="1qiq2xly7r5tv" />
                              <span className="text-sm" data-id="element-7754" data-mp="24cprosvji73y">Take Photo</span>
                            </label>
                            <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="hidden" id="prescription-upload" data-id="element-7755" data-mp="1zjoc0inie3jb" />
                            <label htmlFor="prescription-upload" className="flex flex-col items-center justify-center gap-2 bg-white border-2 border-[#1E2B55] active:bg-blue-50 text-[#1E2B55] font-semibold py-5 rounded-lg cursor-pointer transition-colors" data-id="element-7756" data-mp="2i9w8wj0m7xr2">
                              <FileTextIcon className="w-7 h-7" data-id="element-7757" data-mp="34j2z274q1jva" />
                              <span className="text-sm" data-id="element-7758" data-mp="2qspv0fuyapb8">Choose File</span>
                            </label>
                          </div>
                          {/* Desktop: Drop zone */}
                          <div className="hidden md:block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1E2B55] transition-colors" data-id="element-7759" data-mp="2gujqdqhrf52o">
                            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-7760" data-mp="2luy1jhenqv4x" />
                            <p className="text-sm text-gray-600 mb-2" data-id="element-7761" data-mp="27vg6ikarqln4">
                              Choose file or drag and drop
                            </p>
                            <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="hidden" id="prescription-upload-desktop" data-id="element-7762" data-mp="61z4bw5eqczr" />
                            <label htmlFor="prescription-upload-desktop" className="inline-block bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white font-medium py-2 px-4 rounded-lg cursor-pointer transition-colors" data-id="element-7763" data-mp="37j351ylgnwie">
                              Browse Files
                            </label>
                            <p className="text-xs text-gray-500 mt-3" data-id="element-7764" data-mp="2ouy4f003ornt">
                              Supported: JPG, PNG, PDF • Max 5 MB
                            </p>
                          </div>
                        </>}
                      <p className="text-[11px] text-gray-500 mt-2 md:hidden" data-id="element-7765" data-mp="2audr2jcyfo1p">
                        JPG, PNG, PDF • Max 5 MB
                      </p>
                    </div>
                    {/* Prescription Details */}
                    <div className="order-2" data-id="element-7766" data-mp="ud6hiviwyxss">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7767" data-mp="ug14atpegupd">
                        Prescription Details{' '}
                        <span className="text-gray-400 font-normal" data-id="element-7768" data-mp="14cnmtbu979o8">
                          (optional)
                        </span>
                      </label>
                      <textarea value={prescriptionDetails} onChange={e => setPrescriptionDetails(e.target.value)} placeholder="Medication names, dosage, quantity…" rows={4} className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent text-base" data-id="element-7769" data-mp="6mixjbvq4hpv" />
                    </div>
                  </div>
                </div>
                {/* Payment Method */}
                <div id="section-payment" className="bg-white rounded-xl shadow-md p-6 transition-all scroll-mt-32" data-id="element-7770" data-mp="3h043jvcgg4mf">
                  <h2 className="text-xl font-bold mb-4" data-id="element-7771" data-mp="3qrqd7n959vn2">
                    Select Payment Method
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-7772" data-mp="3lsx8aj7xbcyi">
                    {[{
                    id: 'paytabs',
                    label: 'PayTabs / Tabby'
                  }, {
                    id: 'mada',
                    label: 'Mada'
                  }, {
                    id: 'stcpay',
                    label: 'STCPay'
                  }, {
                    id: 'credit-card',
                    label: 'Credit Card'
                  }, {
                    id: 'cash',
                    label: 'Cash on Delivery'
                  }].map(method => <button key={method.id} onClick={() => setSelectedPaymentMethod(method.id)} className={`border-2 rounded-lg p-4 text-center transition-all ${selectedPaymentMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-7773" data-mp="259xykxerim3y">
                        <CreditCardIcon className={`w-8 h-8 mx-auto mb-2 ${selectedPaymentMethod === method.id ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-7774" data-mp="2nxt63zjs73om" />
                        <span className="text-sm font-medium" data-id="element-7775" data-mp="2f5iwajcst07w">
                          {method.label}
                        </span>
                      </button>)}
                  </div>
                </div>
                {/* Out-of-Stock Handling */}
                <div id="section-out-of-stock" className="bg-white rounded-xl shadow-md p-6 transition-all scroll-mt-32" data-id="element-7776" data-mp="17l42zgtdhzb2">
                  <h2 className="text-xl font-bold mb-4" data-id="element-7777" data-mp="xusm6tolbvmr">
                    If the product is not available
                  </h2>
                  <div className="space-y-3" data-id="element-7778" data-mp="swhdwndy1t6n">
                    {[{
                    id: 'contact',
                    label: 'Contact me by phone'
                  }, {
                    id: 'no-replace',
                    label: 'Do not replace missing items'
                  }, {
                    id: 'alternative',
                    label: 'Choose an alternative if available'
                  }, {
                    id: 'complete',
                    label: 'Complete my order with available items only'
                  }].map(option => <label key={option.id} className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedOutOfStock === option.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-7779" data-mp="2ctq5ny2wgq7o">
                        <input type="radio" name="out-of-stock" value={option.id} checked={selectedOutOfStock === option.id} onChange={e => setSelectedOutOfStock(e.target.value)} className="w-4 h-4 text-[#1E2B55] focus:ring-[#1E2B55]" data-id="element-7780" data-mp="2qld3w3gek8t3" />
                        <span className="ml-3 text-sm font-medium" data-id="element-7781" data-mp="1a6l9jkzp4rh5">
                          {option.label}
                        </span>
                      </label>)}
                  </div>
                  <p className="text-xs text-gray-500 mt-4" data-id="element-7782" data-mp="1qtr9h5a1ac0t">
                    * Delivery charges will apply if the total is below 15 SAR.
                  </p>
                </div>
                {/* Submit / Next-Action Button (Desktop inline) */}
                {(() => {
                const nextStep = getNextRequiredStep();
                const ready = !nextStep;
                return <button onClick={e => {
                  if (isSubmitting) return;
                  if (ready) {
                    handleSubmit(e);
                  } else if (nextStep) {
                    scrollToSection(nextStep.sectionId);
                  }
                }} disabled={isSubmitting} className="hidden md:flex w-full py-4 rounded-lg font-bold text-lg transition-all bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white items-center justify-center gap-2 disabled:opacity-70" aria-label={ready ? 'Submit prescription request' : `Go to ${nextStep!.label}`} data-id="element-7783" data-mp="181ewzppo1ucy">
                      {isSubmitting ? <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-7784" data-mp="2onihirqssf75">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-7785" data-mp="2ft412luj6ymg"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-7786" data-mp="1r9sk8575wcis"></path>
                          </svg>
                          Sending now
                        </> : ready ? <>
                          <CheckCircleIcon className="w-5 h-5" data-id="element-7787" data-mp="2ezwngbgfua99" />
                          Submit Request
                        </> : <>
                          {nextStep!.label}
                          <ChevronRightIcon className="w-5 h-5" data-id="element-7788" data-mp="259bdxsz87lbk" />
                        </>}
                    </button>;
              })()}
              </div>
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-7789" data-mp="2xsiemigehcni">
          <Footer data-id="element-7790" data-mp="mawzip349bzq" />
        </div>
        {/* Mobile Sticky Next-Action / Submit Button */}
        {(() => {
        const nextStep = getNextRequiredStep();
        const ready = !nextStep;
        return <div className="md:hidden fixed bottom-16 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-7791" data-mp="w06t69dlz2py">
              <button onClick={e => {
            if (isSubmitting) return;
            if (ready) {
              handleSubmit(e as unknown as React.FormEvent);
            } else if (nextStep) {
              scrollToSection(nextStep.sectionId);
            }
          }} disabled={isSubmitting} className="w-full py-3.5 rounded-lg font-bold text-base transition-all bg-[#1E2B55] active:bg-[#162041] text-white flex items-center justify-center gap-2 disabled:opacity-70" aria-label={ready ? 'Submit prescription request' : `Go to ${nextStep!.label}`} data-id="element-7792" data-mp="3ckqr4g4a44o">
                {isSubmitting ? <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-7793" data-mp="2anbbwfo03qa">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-7794" data-mp="c7rzu2wzgba9"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-7795" data-mp="pywrahq75x9i"></path>
                    </svg>
                    Sending now
                  </> : ready ? <>
                    <CheckCircleIcon className="w-5 h-5" data-id="element-7796" data-mp="151ep1spd97n7" />
                    Submit Request
                  </> : <>
                    {nextStep!.label}
                    <ChevronRightIcon className="w-5 h-5" data-id="element-7797" data-mp="w3mh59lz529u" />
                  </>}
              </button>
            </div>;
      })()}
        <MobileFooterNav data-id="element-7798" data-mp="1ntqy0yaiz76b" />
        <PharmacistChat data-id="element-7799" data-mp="1nuim7o1sm2mk" />
        {/* Spacer for sticky bottom action + mobile footer nav */}
        <div className="h-32 md:hidden" data-id="element-7800" data-mp="1j1pgawul8o8w"></div>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7801" data-mp="1f1felwdtwjzu" />
        {/* Prescription Details Modal */}
        {selectedPrescription && <PrescriptionDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} prescription={selectedPrescription} onCancelRequest={requestReference => {
        setReferenceNumber(requestReference);
        setIsModalOpen(false);
        setCancelSheetOpen(true);
      }} data-id="element-7802" data-mp="4vbae9sm6g59" />}
        <PrescriptionSuccessModal isOpen={showSuccessModal} referenceNumber={referenceNumber} fulfillmentMethod={fulfillmentMethod} onClose={() => setShowSuccessModal(false)} onViewStatus={() => {
        setShowSuccessModal(false);
        navigate('/prescription/history');
      }} onCancel={() => {
        setShowSuccessModal(false);
        setCancelSheetOpen(true);
      }} data-id="element-7803" data-mp="3i8o0vccm1i8g" />
        <PrescriptionCancelBottomSheet isOpen={cancelSheetOpen} referenceNumber={referenceNumber} onClose={() => setCancelSheetOpen(false)} onConfirm={() => {
        setCancelSheetOpen(false);
        toast.success('Your prescription request has been cancelled.');
        navigate('/prescription/history');
      }} data-id="element-7804" data-mp="1biztixmog247" />
      </div>
    </div>;
}